// pages/_app.tsx
import '../styles/globals.css';
import Header from '../components/Header';
import Spinner from '../components/Spinner';
import { CartContextProvider } from '../lib/CartContext';
import { AuthProvider, useAuth } from '../lib/AuthContext';
import { ThemeProvider } from '../lib/ThemeContext';
import { Toaster } from 'react-hot-toast';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import * as gtag from '../lib/gtag';
import Script from 'next/script';
import GoogleAnalytics from '../components/GoogleAnalytics';
import { LazyMotion, domMax } from 'framer-motion';

const GOOGLE_ADS_ID = gtag.GOOGLE_ADS_ID;
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const CookieConsentBanner = dynamic(
  () => import('../components/CookieConsentBanner'),
  { ssr: false }
);

const Footer = dynamic(() => import('../components/Footer'), { ssr: false });

const ScrollToTopButton = dynamic(
  () => import('../components/ScrollToTopButton').then((m) => m.ScrollToTopButton),
  { ssr: false }
);

const protectedPaths = ['/cart', '/profile', '/account'];

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathOnly = (router.asPath || router.pathname || '')
    .split('?')[0]
    .split('#')[0];
  const isProtected = protectedPaths.some((path) => pathOnly.includes(path));

  useEffect(() => {
    if (!loading) {
      if (!user && isProtected) {
        router.replace(`/login?redirect=${encodeURIComponent(pathOnly)}`);
      } else if (user && !user.emailVerified && isProtected) {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('postVerifyRedirect', router.asPath);
        }
        router.replace('/verify-email');
      }
    }
  }, [user, loading, isProtected, pathOnly, router]);

  if (loading && isProtected) {
    return (
      <div className='min-h-screen flex items-start justify-center pt-24 sm:pt-28'>
        <Spinner />
      </div>
    );
  }

  return <>{children}</>;
}

function App(props: any) {
  const { Component, pageProps } = props;
  const router = useRouter();
  const [hasConsent, setHasConsent] = useState(false);
  const isHomePage = router.pathname === '/';
  const pathOnly = (router.asPath || router.pathname || '').split('?')[0].split('#')[0];
  const isLegalPage = pathOnly.startsWith('/sub/');
  const isShopPage = router.pathname === '/shop';
  const isContactPage = router.pathname === '/contact';
  const isProductPage = router.pathname === '/product/[id]';
  const isAdminPage = router.pathname.startsWith('/admin');
  const isDarkPage = isLegalPage || isShopPage || isContactPage || isProductPage || isAdminPage || router.pathname === '/orders' || router.pathname === '/cart' || router.pathname === '/login' || router.pathname === '/signup' || router.pathname === '/forgot-password' || router.pathname === '/reset-password' || router.pathname === '/reset-password/confirm' || router.pathname === '/verify-email';
  const showNavbar = ![
    '/login', '/signup', '/forgot-password',
  ].some((p) => router.pathname === p || router.asPath.startsWith(p));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const consent = localStorage.getItem('cookieConsent');
      if (consent === 'true') {
        setHasConsent(true);
      }
    }
  }, []);

  const handleRequiredConsent = (accepted: boolean) => {
    if (accepted) {
      localStorage.setItem('cookieConsent', 'true');
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.dataLayer) window.dataLayer = [];
    window.dataLayer.push({ event: 'currency_set', currency: 'EUR', value: null });
  }, [router.asPath, router.pathname]);

  useEffect(() => {
    if (!hasConsent) return;
    const handleRouteChange = (url: string) => gtag.pageview(url);
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [hasConsent, router.events]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <CartContextProvider>
        <LazyMotion features={domMax} strict={false}>
        {/* Google Ads Global Site Tag (gtag.js) - loads on all pages for conversion tracking */}
        {GOOGLE_ADS_ID && (
          <>
            <Script
              src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
              strategy="afterInteractive"
            />
            <Script id="google-ads-gtag" strategy="afterInteractive">
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GOOGLE_ADS_ID}');
              `}
            </Script>
          </>
        )}

        {GTM_ID && (
          <Script id="gtm-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${GTM_ID}');
            `}
          </Script>
        )}

        <div className='relative min-h-screen'>
          <div className='absolute inset-0 -z-10 bg-[#e8e8e8] dark:bg-black' aria-hidden='true' />

          <div className='relative z-10'>
            {showNavbar && <Header />}
            <Toaster 
              position='top-center' 
              containerStyle={{
                top: '80px', // Position below navbar (navbar is ~56px + padding)
              }}
              toastOptions={{
                style: {
                  background: '#000000',
                  color: '#f3f4f6',
                  border: '1px solid #374151',
                  borderRadius: '10px',
                },
                success: {
                  iconTheme: {
                    primary: '#d1d5db',
                    secondary: '#000000',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#f87171',
                    secondary: '#000000',
                  },
                },
              }}
            />
            <main className={isHomePage || isDarkPage ? 'min-h-screen relative' : 'min-h-screen max-w-screen-2xl mx-auto sm:px-6 relative'}>
              <AuthGate>
                <Component {...pageProps} />
              </AuthGate>
            </main>
            <div className='w-full bg-[#e8e8e8] dark:bg-black'>
              <Footer />
            </div>
            <CookieConsentBanner onRequiredConsent={handleRequiredConsent} />
          </div>

          <ScrollToTopButton />
        </div>

        {hasConsent && process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID && (
          <GoogleAnalytics
            measurementId={process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}
            measurementIdLt={process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_LT}
          />
        )}
        </LazyMotion>
        </CartContextProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
